/**
 * Tesoreria › Carte di credito, rendicontazione (1.55, decisions of
 * 10/10/2026): a spending with a card is accounted for when it is linked to
 * the received invoices it pays (they are paid by card on its day) or, with no
 * invoice, given an item of the plan Base. The program proposes, a person
 * confirms; the choices can be remembered as rules, by shop or by category.
 */
import { z } from 'zod';
import { addDays } from './dates.js';
import { merchantKey } from './credit-cards.js';
import { toCents } from './money.js';
import { nameKey } from './tax-ids.js';
import { idSchema, optionalText } from './validation.js';
/**
 * Spending is accounted for from this day; before it, it may be, it is not
 * asked (decision of 10/10/2026). The proposals of an invoice are confirmed
 * together also before it: they close the invoice (2.24, decision of 21/10/2026).
 */
export const CARD_ACCOUNTING_FROM = '2025-01-01';
/**
 * The bank often writes the sign of the shop, not the name of the supplier
 * (2.24, decision of 21/10/2026): an open invoice with the same amount to the
 * cent and the same date is proposed anyway, when it is the only one and the
 * spending is the only one of that day with that amount.
 */
export const CARD_SAME_DAY_REASON = 'stesso importo, stessa data';
/** The key of the spending of a day with an amount, to count them. */
export function cardDayKey(date, amount) {
    return `${date}|${toCents(amount)}`;
}
/**
 * A foreign currency changes the amount by a little: an invoice is proposed
 * up to 2% away and closed at its own amount, the difference being a cost of
 * the exchange (decision of 10/10/2026).
 */
export const CARD_AMOUNT_TOLERANCE = 0.02;
const amount = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .refine((value) => toCents(value) > 0, { message: 'Importo non valido' });
export const cardReconcileSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('fatture'),
        /** Each invoice with the part it is paid by the spending, positive. */
        allocations: z
            .array(z.object({ invoiceId: idSchema, amount }))
            .min(1)
            .max(20),
        /** The shop of the spending becomes a rule: its spending looks for the invoices of this supplier. */
        remember: z.boolean().default(false),
        /**
         * The item of the spending, kept with the invoices (1.57): the item says
         * what the spending is, the invoice what it pays.
         */
        reclassCode: z.string().trim().min(1).max(12).nullable().default(null),
        /** The invoices take the item of the spending in place of theirs (a choice of the user). */
        invoiceReclass: z.boolean().default(false),
    }),
    z.object({
        kind: z.literal('conto'),
        /** An item of the plan Base. */
        reclassCode: z.string().trim().min(1).max(12),
        note: optionalText(300),
        /** The shop of the spending becomes a rule: its spending goes to this item. */
        remember: z.boolean().default(false),
    }),
    z.object({
        /**
         * The instalments of a rateazione it pays (3.21); the item stays on the
         * spending, with the commission the bank wrote inside it.
         */
        kind: z.literal('rata'),
        dueIds: z.array(idSchema).min(1).max(20),
        reclassCode: z.string().trim().min(1).max(12),
    }),
    z.object({
        /**
         * Given to a subject without a document (2.25): the top-up of a prepaid
         * credit (EasyPark), used later on his invoices like a bank payment (2.22).
         */
        kind: z.literal('soggetto'),
        subjectId: idSchema,
        note: optionalText(300),
    }),
]);
/** «Non arriverà fattura» (3.22), or the invoice awaited again. */
export const cardNoInvoiceSchema = z.object({ noInvoice: z.boolean() });
/** Several spending confirmed together (the proposals selected). */
export const cardReconcileManySchema = z.object({
    items: z
        .array(z.object({ movementId: idSchema, request: cardReconcileSchema }))
        .min(1)
        .max(2000),
});
export const CARD_RULE_KINDS = ['fornitore', 'categoria'];
export const cardRuleSchema = z
    .object({
    kind: z.enum(CARD_RULE_KINDS),
    /** The shop (merchantKey of the description) or the code of the category. */
    key: z.string().trim().min(1).max(200),
    /** For a shop: the supplier whose invoices it pays… */
    subjectId: idSchema.nullable(),
    /** …and the item of the plan Base its spending goes to: both, or one of the two (1.57). */
    reclassCode: z.string().trim().min(1).max(12).nullable(),
})
    .superRefine((body, context) => {
    if (body.subjectId === null && body.reclassCode === null) {
        context.addIssue({
            code: 'custom',
            path: ['reclassCode'],
            message: 'Un fornitore, un conto o tutti e due',
        });
    }
    if (body.kind === 'categoria' && body.subjectId !== null) {
        context.addIssue({
            code: 'custom',
            path: ['subjectId'],
            message: 'Una categoria propone solo un conto',
        });
    }
});
export const cardShopQuerySchema = z.object({ key: z.string().trim().min(1).max(200) });
/**
 * The spending of a shop given at once to a subject without a document (2.25):
 * the top-ups of a prepaid credit; those already with invoices stay.
 */
export const cardShopSubjectSchema = z.object({
    key: z.string().trim().min(1).max(200),
    subjectId: idSchema,
});
/** An item for the shop: its rule, and the spending not accounted for given it at once. */
export const cardShopItemSchema = z.object({
    key: z.string().trim().min(1).max(200),
    reclassCode: z.string().trim().min(1).max(12),
    apply: z.boolean().default(true),
});
const LEGAL_WORDS = new Set([
    'srl',
    'srls',
    'spa',
    'sas',
    'snc',
    'ltd',
    'limited',
    'inc',
    'llc',
    'gmbh',
    'bv',
    'sarl',
    'societa',
    'italia',
    'italy',
    'europe',
    'emea',
    'ireland',
    'www',
    'com',
]);
function words(text) {
    return nameKey(text)
        .replace(/[^a-z0-9 ]+/g, ' ')
        .split(' ')
        .filter((word) => word.length >= 4 && !LEGAL_WORDS.has(word));
}
/** Whether the shop of the bank and the name of the supplier share a meaningful word. */
export function shopMatchesSupplier(description, supplier) {
    const shop = new Set(words(merchantKey(description)));
    return words(supplier).some((word) => shop.has(word));
}
/**
 * The proposal for a spending, if any: first the invoices (of the supplier of
 * its rule, or of a supplier whose name the bank writes, with the same
 * amount), then the item of its rule, then that of its category. A refund
 * goes where the spending it gives back went.
 */
export function cardProposal(spending, context) {
    const cents = toCents(spending.amount);
    const key = merchantKey(spending.description);
    const rule = context.shops.get(key);
    const conto = (reclassCode, reasons) => context.items.has(reclassCode)
        ? {
            request: { kind: 'conto', reclassCode, note: null, remember: false },
            invoices: [],
            reasons,
        }
        : null;
    if (cents < 0) {
        const original = context.accounted.find((item) => item.cardId === spending.cardId &&
            item.key === key &&
            toCents(item.amount) >= -cents &&
            item.date <= spending.date &&
            item.date >= addDays(spending.date, -120));
        if (original)
            return conto(original.reclassCode, ['come la spesa rimborsata']);
    }
    else {
        const match = matchingInvoice(spending, cents, rule?.subjectId ?? null, context);
        if (match) {
            const { invoice } = match;
            const exact = toCents(invoice.residual) === cents;
            return {
                request: {
                    kind: 'fatture',
                    allocations: [{ invoiceId: invoice.id, amount: invoice.residual }],
                    remember: false,
                    reclassCode: spending.reclassCode ?? rule?.reclassCode ?? null,
                    invoiceReclass: false,
                },
                invoices: [
                    {
                        id: invoice.id,
                        number: invoice.number,
                        counterpartyName: invoice.counterpartyName,
                        date: invoice.date,
                        residual: invoice.residual,
                        reclassCode: invoice.reclassCode,
                    },
                ],
                reasons: match.sameDay
                    ? [CARD_SAME_DAY_REASON]
                    : [
                        rule?.subjectId ? 'fornitore dalla regola' : 'nome del fornitore',
                        exact ? 'importo uguale' : 'importo vicino (cambio)',
                    ],
            };
        }
        // A shop of a supplier with no item: its invoice is not there yet, nothing else is proposed.
        if (rule?.subjectId && !rule.reclassCode)
            return null;
    }
    if (rule?.reclassCode)
        return conto(rule.reclassCode, ['dalla regola del fornitore']);
    const byCategory = spending.category ? context.categories.get(spending.category) : undefined;
    return byCategory ? conto(byCategory, ['dalla categoria']) : null;
}
/**
 * The open received invoice the spending pays: of the supplier of the rule
 * within the tolerance, otherwise of a supplier named by the bank with the
 * same amount; dated from 45 days before to 10 after. Without a rule or a
 * name, the only invoice of the same day with the same amount (2.24).
 * Self-billing invoices are left out for now (decision of 10/10/2026).
 */
function matchingInvoice(spending, cents, subjectId, context) {
    const from = addDays(spending.date, -45);
    const to = addDays(spending.date, 10);
    const tolerance = Math.round(cents * CARD_AMOUNT_TOLERANCE);
    let best = null;
    const sameDay = [];
    for (const invoice of context.invoices) {
        if (invoice.direction !== 'ricevuta' ||
            invoice.kind !== 'fattura' ||
            invoice.state === 'saldata' ||
            invoice.date < from ||
            invoice.date > to ||
            (invoice.origin === 'apertura' && context.openingDate && spending.date < context.openingDate))
            continue;
        const residual = toCents(invoice.residual);
        if (residual <= 0)
            continue;
        const difference = Math.abs(residual - cents);
        // A self-billing (3.17) is in euro at the exchange of its day: the shop paid at another.
        const allowed = invoice.selfBilling ? tolerance : 0;
        if (subjectId && (invoice.subjectId === subjectId || !invoice.selfBilling)) {
            if (invoice.subjectId !== subjectId || difference > tolerance)
                continue;
        }
        else if (difference > allowed) {
            continue;
        }
        else if (
        // The name of the invoice or an alias of its subject (1.73).
        ![invoice.counterpartyName, ...(invoice.aliases ?? [])].some((name) => shopMatchesSupplier(spending.description, name))) {
            if (difference === 0 && invoice.date === spending.date)
                sameDay.push(invoice);
            continue;
        }
        // The nearest amount first, then the nearest date.
        const days = Math.abs(Date.parse(invoice.date) - Date.parse(spending.date)) / 86_400_000;
        const score = difference * 1000 + days;
        if (!best || score < best.score)
            best = { invoice, score };
    }
    if (best)
        return { invoice: best.invoice, sameDay: false };
    const spendingOfTheDay = context.sameDay?.get(cardDayKey(spending.date, spending.amount)) ?? 1;
    return sameDay.length === 1 && spendingOfTheDay === 1
        ? { invoice: sameDay[0], sameDay: true }
        : null;
}
/** Whether invoices of this total may pay a spending: up to 2% more, for the exchange. */
export function allocationsFit(spending, allocations) {
    const cents = toCents(spending);
    const total = allocations.reduce((sum, item) => sum + toCents(item.amount), 0);
    return total <= cents + Math.round(cents * CARD_AMOUNT_TOLERANCE);
}
//# sourceMappingURL=card-reconciliation.js.map